import type { FmRankedListItem } from '../FmRankedList';
import { paletteSpecies } from '../charts/palette';
import type { FmTableColumn } from '../FmTable';
import { isFmTableStructuredCell, renderFmTableCell } from '../FmTableCell';
import type { FmTimelineItem, FmTimelineKind } from '../FmTimeline';
import type {
  FmStoryAccentTone,
  FmStoryRankedRow,
  FmStoryTableDescriptor,
  FmStoryTableRow,
  FmStoryTimelineEvent,
} from './elementFields.types';

/**
 * Pure adapters from served element payloads to the shared primitives' item
 * shapes, used by the element renderers and the story-section renderer.
 */

/**
 * A served rank string becomes the list's ordinal; a signed delta becomes its
 * direction and stays readable beside the row's status.
 */
export function toRankedListItems(rows: readonly FmStoryRankedRow[]): FmRankedListItem[] {
  return rows.map((row) => ({
    key: row.key ?? row.rank,
    rank: Number.parseInt(row.rank, 10),
    label: row.label,
    sublabel: [row.delta, row.status].filter(Boolean).join(' · ') || undefined,
    value: row.value,
    swatch: row.speciesCode === undefined ? undefined : paletteSpecies(row.speciesCode),
    // Intl formats negatives with U+2212 MINUS SIGN in fi/sv; both minus glyphs read as down.
    indicator: row.delta ? (/^[-\u2212]/.test(row.delta) ? 'down' : 'up') : undefined,
    accent: row.selected ?? row.tone === 'accent',
    selected: row.selected,
    onClick: row.onClick,
    'data-testid': row.testId,
  }));
}

const TIMELINE_ACCENT: Record<FmStoryAccentTone, string> = {
  accent: 'var(--fm-accent)',
  teal: 'var(--fm-teal)',
  amber: 'var(--fm-amber)',
  sky: 'var(--fm-sky)',
  rose: 'var(--fm-rose)',
  neutral: 'var(--fm-d-fg-3)',
};

/** Phase from the served event; the tone stands in only where the builder gave no phase. */
function timelinePhase(event: FmStoryTimelineEvent): FmTimelineKind {
  if (event.phase) return event.phase;
  if (event.tone === 'sky') return 'current';
  if (event.tone === 'amber') return 'future';
  return 'past';
}

export function toTimelineItem(event: FmStoryTimelineEvent, index: number): FmTimelineItem {
  const phase = timelinePhase(event);
  return {
    id: `${event.year}-${event.label}-${index}`,
    year: event.year,
    title: event.label,
    meta: event.meta ?? event.detail,
    kind: phase,
    // The chip prints only the builder's localized label; a beat without one is
    // told apart by its dot shape, never by an English stand-in.
    ...(event.phaseLabel ? { kindLabel: event.phaseLabel } : {}),
    accentColor: TIMELINE_ACCENT[event.tone ?? 'neutral'],
    content: event.content ?? (event.meta ? event.detail : undefined),
    testId: event.testId,
  };
}

type FmTableColumnSource = Pick<
  FmStoryTableDescriptor,
  'columns' | 'rowActions' | 'actionHeader' | 'locale' | 'emptyCellLabel'
>;

/**
 * Served columns become table columns; row actions become the governed action
 * rail. A structured cell renders through the table's cell vocabulary, and a
 * host's inhibition disables every action not marked as read-only.
 */
export function buildTableColumns(
  table: FmTableColumnSource,
  inhibitionReason: string | null = null,
): FmTableColumn<FmStoryTableRow>[] {
  const cellOptions = { emptyLabel: table.emptyCellLabel ?? '', locale: table.locale };
  const columns: FmTableColumn<FmStoryTableRow>[] = table.columns.map((column) => ({
    key: column.key,
    header: column.header,
    label: column.header,
    mobileLabel: column.mobileLabel,
    mobileHidden: column.mobileHidden,
    numeric: column.numeric,
    sortable: column.sortable,
    role: column.role,
    render: (row) => {
      const value = row[column.key];
      return isFmTableStructuredCell(value)
        ? renderFmTableCell(value, cellOptions)
        : String(value ?? '');
    },
  }));
  if (!table.rowActions) return columns;
  const rowActions = table.rowActions;
  const actionHeader = table.actionHeader;
  if (!actionHeader) {
    throw new Error(
      'A table with row actions requires actionHeader; there is no locale-neutral column name.',
    );
  }
  return [
    ...columns,
    {
      key: '__actions',
      header: actionHeader,
      label: actionHeader,
      role: 'actions',
      actions: (row) =>
        rowActions(row).map((action) => ({
          id: action.id,
          label: action.label,
          intent: action.intent ?? 'inspect',
          disabledReason:
            action.disabledReason ??
            (action.availableWhileInhibited ? undefined : (inhibitionReason ?? undefined)),
          confirm: action.confirm,
          onSelect: action.onSelect,
          'data-testid': action.testId,
        })),
      actionPolicy: 'primary-plus-menu',
    },
  ];
}
